import { resolveUsgOrigin } from '@lib/http/commerceAxiosClient';
import { useSession } from 'next-auth/react';
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { isValidToken } from './bridgeTokenCookies';
import {
  ensureBridgeToken,
  hasUsableBridgeToken,
  publishActiveBridgeTokenSubject,
} from './ensureBridgeToken';

/**
 * Re-render signal only. Readiness itself is never remembered — it is read
 * back off the cookies each render, so an access token that expired while the
 * provider was unmounted cannot leave a stale `ready` behind.
 */
let revision = 0;
const listeners = new Set<() => void>();

const publishProvisioningOutcome = () => {
  revision += 1;
  listeners.forEach((listener) => listener());
};

/**
 * Readiness is also re-read when the tab is shown again: an access cookie
 * can expire while it is hidden, and no session refetch re-renders the gate
 * on focus under MULTI_CART (CPS-7696).
 */
const onVisibilityChange = () => {
  if (document.visibilityState === 'visible') publishProvisioningOutcome();
};

const subscribe = (listener: () => void) => {
  if (listeners.size === 0) {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
  };
};

const useProvisioningRevision = () =>
  useSyncExternalStore(
    subscribe,
    () => revision,
    () => 0
  );

const useSessionIdentity = () => {
  const { data: session, status } = useSession();
  // The platform mint bearers the Unity ID JWT, not the opaque Genesis token,
  // which it rejects with a 401.
  const unityToken = session?.accessToken?.unity_token;
  const sessionId =
    session?.accessToken?.bridge_session_id ??
    session?.accessToken?.genesis_access_token;
  const genesisUser = session?.accessToken?.genesis_user;
  const userId = typeof genesisUser === 'string' ? genesisUser : undefined;

  // Tells the request path whose token it may use, and hands it the bearer
  // the gateway wants alongside it; until the session resolves it stays
  // unknown and the gate leaves the cookies alone. Published during render,
  // not from an effect: this same render can report `isReady`, and a
  // descendant's effect — which React runs before this component's — would
  // otherwise request with the subject still unknown and go out as a guest.
  // Idempotent, so a repeated render costs nothing.
  if (status !== 'loading') {
    publishActiveBridgeTokenSubject({
      userId,
      sessionId,
      unityToken: isValidToken(unityToken) ? unityToken : undefined,
    });
  }

  return {
    sessionStatus: status,
    sessionId,
    unityToken: isValidToken(unityToken) ? unityToken : undefined,
    userId,
  };
};

/**
 * Imperative gate: resolve the signed-in user's bridge token before letting a
 * bridge request leave the browser. Resolves to `false` when there is none,
 * and wakes the provider's org-id gate so a late success is picked up. A
 * signed-out visitor needs no token (their cart calls go through the guest
 * cart route), so it resolves `true` once the session has.
 */
export const useEnsureBridgeToken = () => {
  const { sessionStatus, unityToken, userId, sessionId } = useSessionIdentity();
  const isSessionResolved = sessionStatus !== 'loading';

  return useCallback(async (): Promise<boolean> => {
    if (!isSessionResolved) {
      return false;
    }
    if (sessionStatus === 'unauthenticated') {
      return true;
    }
    try {
      return Boolean(
        await ensureBridgeToken({
          usgBaseUrl: resolveUsgOrigin(),
          unityToken,
          userId,
          sessionId,
        })
      );
    } finally {
      publishProvisioningOutcome();
    }
  }, [sessionStatus, unityToken, userId, sessionId, isSessionResolved]);
};

/**
 * Provisions the signed-in user's bridge token on mount. commerce-core's
 * OrganizationProvider issues `/commerce/bridge/v1/users/org/{orgId}` as soon
 * as it has an organizationId — before any Buy Now click — so callers must
 * withhold the org id until this reports ready.
 *
 * Readiness drops when the access cookie expires mid-session, and that is
 * intended (CPS-7883). It is read back off the cookie on every render because
 * a remembered `ready` outlives the token it vouched for, and reporting ready
 * off the refresh cookie instead would release the org id before anything has
 * been provisioned, widening a gate that has to stay closed until then. What
 * made the drop costly was that nothing re-provisioned until the next Buy Now
 * click, so a drop now starts provisioning straight away: the org id is held
 * back for one refresh round trip, not for the rest of the visit, and a
 * renewal that fails leaves the gate closed rather than the request
 * unauthenticated.
 *
 * `enabled: false` provisions nothing, for readers that render with the
 * MULTI_CART flag off.
 */
export const useBridgeTokenProvisioning = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  const { sessionStatus, userId } = useSessionIdentity();
  const ensure = useEnsureBridgeToken();
  useProvisioningRevision();
  // Bind completion to this session's ensure callback so an earlier user's
  // failure cannot leak into a new session while its provisioning is pending.
  const [settledEnsure, setSettledEnsure] = useState<typeof ensure>();
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => {
    setSettledEnsure(undefined);
    setAttempt((value) => value + 1);
  }, []);

  const isReady =
    // A signed-out visitor has no org to withhold and needs no token.
    sessionStatus === 'unauthenticated' ||
    (sessionStatus === 'authenticated' && hasUsableBridgeToken(userId));

  // Re-runs whenever readiness drops, not only on mount: an expired access
  // cookie is renewed from the refresh cookie without waiting for Buy Now.
  useEffect(() => {
    if (!enabled || sessionStatus !== 'authenticated') {
      return;
    }
    // A usable token supersedes the last outcome, so the next renewal does
    // not report that outcome as a failure while it is still pending.
    if (isReady) {
      setSettledEnsure(undefined);
      return;
    }
    let active = true;
    void ensure().then(() => {
      if (active) setSettledEnsure(() => ensure);
    });
    return () => {
      active = false;
    };
  }, [enabled, sessionStatus, ensure, isReady, attempt]);

  return {
    isReady,
    isError:
      enabled &&
      sessionStatus === 'authenticated' &&
      !isReady &&
      settledEnsure === ensure,
    retry,
  };
};
